import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, ExternalLink, CheckCircle2 } from "lucide-react";
import { createRegistration, getRegistrationStatus, getPublicConfig } from "@/lib/api";
import { track } from "@/lib/tracker";
import { getStoredReferrer } from "@/lib/referrer";
import { useSiteContent } from "@/lib/siteContent";

const INTERESTS = [
  { value: "website", label: "Website" },
  { value: "landing_page", label: "Landing Page" },
  { value: "funnel", label: "Funnel" },
  { value: "app_prototype", label: "App Prototype" },
  { value: "intake_system", label: "Intake System" },
  { value: "dashboard", label: "Dashboard" },
];

const INPUT_CLS =
  "bg-white/[0.04] border border-white/15 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-2 focus:ring-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-0 h-12";

export default function RegistrationForm() {
  const sc = useSiteContent();
  const [submitting, setSubmitting] = useState(false);
  const [cap, setCap] = useState({ can_register: true, cta_label: "Save My Seat", message: "" });
  const [paymentLink, setPaymentLink] = useState("");
  const [reg, setReg] = useState(null); // set after successful registration submit
  const [touched, setTouched] = useState(false);
  const [fromLink, setFromLink] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", business_name: "", what_to_build: "", interest: "", referred_by: "",
  });

  useEffect(() => {
    getRegistrationStatus().then(setCap).catch(() => {});
    getPublicConfig().then((cfg) => setPaymentLink(cfg?.payment_link || "")).catch(() => {});
    const ref = getStoredReferrer();
    if (ref) {
      setForm((s) => (s.referred_by ? s : { ...s, referred_by: ref }));
      setFromLink(true);
    }
  }, []);

  const update = (k, v) => {
    if (!touched) { setTouched(true); track("form_start", { form: "registration" }); }
    setForm((s) => ({ ...s, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!cap.can_register) { toast.error(cap.message || "Registration is closed."); return; }
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.what_to_build || !form.interest) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createRegistration(form);
      track("form_complete", { form: "registration", registration_id: created.id });
      setReg(created);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
      getRegistrationStatus().then(setCap).catch(() => {});
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Step 2: registration saved, show payment link button -----
  if (reg) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="registration-success-step">
        <div className="p-7 sm:p-10 rounded-3xl bg-white/[0.03] border border-white/10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 border border-white/20 mb-6">
            <CheckCircle2 className="text-white" size={26} />
          </div>
          <h3 className="font-display text-3xl sm:text-4xl tracking-[-0.025em] text-white mb-3">
            Registration saved.
          </h3>
          <p className="text-white/75 leading-relaxed max-w-md mx-auto">
            <strong className="text-white">{reg.first_name}</strong>, your information is in.
            Now click below to complete your <strong className="text-white">{sc.lab_price_label} payment</strong> securely through Stripe. <span className="text-white/85">Klarna &amp; Afterpay available at checkout.</span>
          </p>

          <div className="mt-8">
            <a
              href={paymentLink || "#"}
              data-testid="save-my-seat-payment-link"
              data-cta="payment-link"
              onClick={() => track("checkout_start", { registration_id: reg.id })}
              className="inline-block"
            >
              <Button
                disabled={!paymentLink}
                className="h-14 px-8 rounded-full bg-white text-ink hover:bg-white/90 font-medium text-base disabled:opacity-60"
              >
                Save My Seat — Pay {sc.lab_price_label} with Stripe
                <ExternalLink className="ml-2" size={16} />
              </Button>
            </a>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-white/55">
            <ShieldCheck size={13} />
            <span>Payment is securely processed by Stripe. We never see your card details.</span>
          </div>

          <p className="text-xs text-white/45 mt-6 max-w-md mx-auto leading-relaxed">
            Your seat is not yet confirmed. It will be confirmed after we verify your payment in Stripe and you receive your official receipt by email.
          </p>
        </div>
      </div>
    );
  }

  // ----- Step 1: registration form -----
  return (
    <div className="max-w-2xl mx-auto">
      {!cap.can_register && (
        <div data-testid="registration-closed-banner" className="mb-6 p-4 rounded-2xl bg-white/5 border border-white/15 text-center">
          <div className="font-display text-xl text-white">{cap.cta_label}</div>
          <div className="text-sm text-white/65 mt-1">{cap.message}</div>
        </div>
      )}

      <form onSubmit={onSubmit} data-testid="registration-form" className="p-7 sm:p-10 rounded-3xl bg-white/[0.025] border border-white/10 space-y-5 backdrop-blur-sm">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="First Name" htmlFor="first_name">
            <Input id="first_name" data-testid="input-first-name" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className={INPUT_CLS} />
          </Field>
          <Field label="Last Name" htmlFor="last_name">
            <Input id="last_name" data-testid="input-last-name" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className={INPUT_CLS} />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Email" htmlFor="email">
            <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={INPUT_CLS} />
          </Field>
          <Field label="Phone" htmlFor="phone">
            <Input id="phone" type="tel" data-testid="input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={INPUT_CLS} />
          </Field>
        </div>

        <Field label="Business / Organization Name" htmlFor="business_name" optional>
          <Input id="business_name" data-testid="input-business-name" value={form.business_name} onChange={(e) => update("business_name", e.target.value)} className={INPUT_CLS} />
        </Field>

        <Field label={`Referred By${fromLink ? " · auto-filled from your link" : ""}`} htmlFor="referred_by" optional>
          <Input
            id="referred_by"
            data-testid="input-referred-by"
            value={form.referred_by}
            onChange={(e) => update("referred_by", e.target.value)}
            placeholder="Name of the person who told you about the lab"
            className={INPUT_CLS}
          />
        </Field>

        <Field label="What do you want to build?" htmlFor="what_to_build">
          <Textarea id="what_to_build" data-testid="input-what-to-build" rows={3} value={form.what_to_build} onChange={(e) => update("what_to_build", e.target.value)} placeholder="A website, landing page, lead funnel, app prototype, intake system, or dashboard..." className={`${INPUT_CLS} !h-auto resize-none py-3`} />
        </Field>

        <Field label="Most interested in" htmlFor="interest">
          <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
            <SelectTrigger id="interest" data-testid="input-interest" className={INPUT_CLS}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent className="bg-ink border-white/15 text-white">
              {INTERESTS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`interest-${o.value}`} className="focus:bg-white/10 focus:text-white">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Button
          type="submit"
          data-testid="submit-registration"
          data-cta="form-submit"
          disabled={submitting || !cap.can_register}
          className="w-full h-14 rounded-full bg-white text-ink hover:bg-white/90 font-medium text-base disabled:opacity-60 mt-2 transition-transform hover:-translate-y-0.5"
        >
          {submitting ? (
            <>
              <Loader2 className="animate-spin mr-2" size={18} /> Saving registration...
            </>
          ) : (
            <>
              {cap.can_register ? "Continue to Payment" : cap.cta_label}
              {cap.can_register && <ArrowRight className="ml-2" size={18} />}
            </>
          )}
        </Button>

        <p className="text-xs text-white/50 text-center leading-relaxed pt-2">
          You are not registered until payment is complete. Payment is processed securely through Stripe; we never see your card details.
        </p>
      </form>
    </div>
  );
}

function Field({ label, htmlFor, optional, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-medium font-mono">
        {label}{optional ? "" : " *"}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
